import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BillingService, BillingSummary, BillingLedgerEntry } from '@/pages/service/core/billing.service';
import { AppDatePipe } from '../../service/core/app-date.pipe';

/**
 * Company-facing wallet view embedded in Settings → Billing. Self-fetches the
 * balance snapshot + ledger when rendered. Read-only: top-ups are handled by
 * the platform team, not self-serve here.
 */
@Component({
    selector: 'app-billing-overview',
    standalone: true,
    imports: [CommonModule, TableModule, TagModule, ButtonModule, TooltipModule, ProgressSpinnerModule, AppDatePipe],
    template: `
    <div class="bill">
        <div class="bill-head">
            <div>
                <h3 class="bill-title">Wallet &amp; Usage</h3>
                <p class="bill-sub">Your credit balance, what you've spent, and every transaction.</p>
            </div>
            <button pButton icon="pi pi-refresh" [text]="true" severity="secondary" (click)="load()" pTooltip="Refresh"></button>
        </div>

        <div *ngIf="loading" class="bill-loading"><p-progressSpinner strokeWidth="4" styleClass="bill-spin"></p-progressSpinner></div>

        <ng-container *ngIf="!loading">
            <!-- Status banner -->
            <div class="bill-alert bill-alert--danger" *ngIf="summary?.status === 'OVERDRAWN'">
                <i class="pi pi-exclamation-triangle"></i>
                <span>Your balance is empty, so sending is paused. AI keeps replying until it reaches
                    <b>−₹{{ summary?.aiOverdraftLimit | number:'1.0-2' }}</b>, then it pauses too. Top up to resume.</span>
            </div>
            <div class="bill-alert bill-alert--warn" *ngIf="summary?.status === 'LOW'">
                <i class="pi pi-info-circle"></i>
                <span>Your balance is running low. Top up soon to avoid interruptions to sends and AI.</span>
            </div>

            <!-- Stat tiles -->
            <div class="bill-tiles">
                <div class="bill-tile">
                    <span class="t-label">Balance</span>
                    <span class="t-value" [class.neg]="(summary?.balance || 0) < 0">₹{{ summary?.balance | number:'1.2-2' }}</span>
                    <span class="t-pill" [ngClass]="statusClass">{{ statusLabel }}</span>
                </div>
                <div class="bill-tile">
                    <span class="t-label">AI headroom
                        <i class="pi pi-question-circle" pTooltip="AI keeps running until this reaches zero — your balance plus a buffer of 10% of your last top-up." tooltipPosition="top"></i>
                    </span>
                    <span class="t-value">₹{{ summary?.aiHeadroom | number:'1.2-2' }}</span>
                    <span class="t-hint">incl. ₹{{ summary?.aiOverdraftLimit | number:'1.0-2' }} overdraft buffer</span>
                </div>
                <div class="bill-tile">
                    <span class="t-label">Spent · last 30 days</span>
                    <span class="t-value">₹{{ summary?.spend30dTotal | number:'1.2-2' }}</span>
                    <span class="t-hint" *ngIf="summary?.updatedAt">Updated {{ summary?.updatedAt | appDate:'datetime' }}</span>
                </div>
            </div>

            <!-- Usage breakdown -->
            <div class="bill-panel" *ngIf="summary?.spend30d?.length">
                <div class="panel-cap">Where it went · last 30 days</div>
                <div class="usage-row" *ngFor="let s of summary?.spend30d">
                    <span class="u-label">{{ s.label || s.category }}</span>
                    <div class="u-bar"><div class="u-fill" [style.width.%]="pct(s.amount)"></div></div>
                    <span class="u-amt">₹{{ s.amount | number:'1.2-2' }}</span>
                </div>
            </div>
            <div class="bill-panel bill-empty" *ngIf="!summary?.spend30d?.length">No usage in the last 30 days.</div>

            <!-- Top-up note -->
            <div class="bill-note">
                <i class="pi pi-info-circle"></i>
                <span>To add credits, contact your account manager. Top-ups are applied to your wallet and show up here right away.</span>
            </div>

            <!-- Ledger -->
            <div class="bill-panel">
                <div class="panel-cap">Recent transactions</div>
                <p-table [value]="ledger" [rows]="10" [paginator]="ledger.length > 10" styleClass="p-datatable-sm bill-table">
                    <ng-template pTemplate="header">
                        <tr><th>When</th><th>Type</th><th>Detail</th><th class="r">Amount</th><th class="r">Balance</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-e>
                        <tr>
                            <td class="muted">{{ e.createdAt ? (e.createdAt | appDate:'datetime') : '—' }}</td>
                            <td><p-tag [value]="e.type" [severity]="e.type === 'CREDIT' ? 'success' : 'secondary'"></p-tag></td>
                            <td class="muted">{{ e.reason || e.refType || '—' }}</td>
                            <td class="r" [class.credit]="e.type === 'CREDIT'" [class.debit]="e.type === 'DEBIT'">
                                {{ e.type === 'CREDIT' ? '+' : '−' }}₹{{ e.amount | number:'1.2-4' }}
                            </td>
                            <td class="r muted">₹{{ e.balanceAfter | number:'1.2-2' }}</td>
                        </tr>
                    </ng-template>
                </p-table>
                <div class="bill-empty" *ngIf="!ledger.length">No transactions yet.</div>
            </div>
        </ng-container>
    </div>
    `,
    styles: [`
        .bill { display:flex; flex-direction:column; gap:16px; }
        .bill-head { display:flex; align-items:flex-start; justify-content:space-between; }
        .bill-title { margin:0; font-size:var(--gh-fs-16); font-weight:700; color:var(--text-color, var(--gh-text)); }
        .bill-sub { margin:3px 0 0; color:var(--text-color-secondary, var(--gh-text-3)); font-size:var(--gh-fs-13); }
        .bill-loading { display:flex; justify-content:center; padding:28px; }
        :host ::ng-deep .bill-spin { width:34px; height:34px; }

        .bill-alert { display:flex; align-items:flex-start; gap:10px; padding:11px 14px; border-radius:var(--gh-r-md); font-size:var(--gh-fs-13); line-height:1.4; border:1px solid transparent; }
        .bill-alert i { margin-top:1px; }
        .bill-alert--danger { background:rgba(220,38,38,.06); border-color:rgba(220,38,38,.35); color:var(--gh-danger); }
        .bill-alert--warn { background:rgba(217,119,6,.06); border-color:rgba(217,119,6,.35); color:var(--gh-warn); }

        .bill-tiles { display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; }
        .bill-tile { display:flex; flex-direction:column; gap:6px; padding:14px 16px; border:1px solid var(--surface-border, var(--gh-border)); border-radius:var(--gh-r-md); background:transparent; }
        .t-label { font-size:var(--gh-fs-11); text-transform:uppercase; letter-spacing:.05em; color:var(--text-color-secondary, var(--gh-text-3)); font-weight:600; display:flex; align-items:center; gap:6px; }
        .t-label i { font-size:var(--gh-fs-12); cursor:help; color:var(--text-color-secondary, var(--gh-text-muted)); }
        .t-value { font-size:1.5rem; font-weight:700; color:var(--text-color, var(--gh-text)); }
        .t-value.neg { color:var(--gh-danger); }
        .t-hint { font-size:var(--gh-fs-12); color:var(--text-color-secondary, var(--gh-text-muted)); }
        .t-pill { align-self:flex-start; font-size:var(--gh-fs-11); font-weight:700; text-transform:uppercase; letter-spacing:.05em; padding:2px 9px; border-radius:var(--gh-r-pill); border:1px solid transparent; }
        .t-pill.ok { background:rgba(5,150,105,.1); color:var(--gh-primary); border-color:rgba(5,150,105,.3); }
        .t-pill.low { background:rgba(217,119,6,.1); color:var(--gh-warn); border-color:rgba(217,119,6,.3); }
        .t-pill.over { background:rgba(220,38,38,.1); color:var(--gh-danger); border-color:rgba(220,38,38,.3); }
        .t-pill.unl { background:rgba(99,102,241,.1); color:var(--gh-accent); border-color:rgba(99,102,241,.3); }

        .bill-panel { border:1px solid var(--surface-border, var(--gh-border)); border-radius:var(--gh-r-md); padding:14px 16px; background:transparent; }
        .panel-cap { font-size:var(--gh-fs-12); text-transform:uppercase; letter-spacing:.05em; color:var(--text-color-secondary, var(--gh-text-3)); font-weight:700; margin-bottom:12px; }

        .usage-row { display:grid; grid-template-columns:120px 1fr 96px; align-items:center; gap:12px; padding:5px 0; }
        .u-label { font-size:var(--gh-fs-13); font-weight:600; color:var(--text-color, var(--gh-text-2)); }
        .u-bar { height:8px; border-radius:var(--gh-r-pill); background:var(--surface-200, var(--gh-surface-hover)); overflow:hidden; }
        .u-fill { height:100%; border-radius:var(--gh-r-pill); background:var(--primary-color, var(--gh-accent)); }
        .u-amt { text-align:right; font-size:var(--gh-fs-13); font-weight:600; color:var(--text-color, var(--gh-text)); }

        .bill-note { display:flex; align-items:flex-start; gap:9px; font-size:var(--gh-fs-13); color:var(--text-color-secondary, var(--gh-text-3)); padding:2px 2px; }
        .bill-note i { margin-top:2px; }

        .bill-empty { color:var(--text-color-secondary, var(--gh-text-muted)); font-style:italic; font-size:var(--gh-fs-14); }
                ::ng-deep .bill-table .p-datatable-thead > tr > th {
  background: var(--gh-surface-sunken);
  color: var(--gh-text-2);
  font-size: var(--gh-fs-11);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800;
  padding: 0 14px;
  border-bottom: 1px solid var(--gh-border);
  font-family: var(--gh-font-ui);
  height: 38px;
  white-space: nowrap;
  vertical-align: middle;
}
                ::ng-deep .bill-table .p-datatable-tbody > tr > td {
  padding: 5px 14px;
  border-bottom: 1px solid var(--gh-divider);
  font-size: var(--gh-fs-12);
  height: 40px;
  font-weight: 500;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
}
        .r { text-align:right; }
        .muted { color:var(--text-color-secondary, var(--gh-text-3)); }
        .credit { color:var(--gh-primary); font-weight:600; }
        .debit { color:var(--gh-warn); font-weight:600; }

        @media (max-width: 860px) { .bill-tiles { grid-template-columns:1fr; } .usage-row { grid-template-columns:96px 1fr 84px; } }
    `]
})
export class BillingOverviewComponent implements OnInit {
    loading = false;
    summary: BillingSummary | null = null;
    ledger: BillingLedgerEntry[] = [];

    constructor(private billing: BillingService) {}

    ngOnInit(): void { this.load(); }

    load(): void {
        this.loading = true;
        this.billing.getSummary().subscribe({
            next: (s) => { this.summary = s ?? null; this.loading = false; },
            error: () => { this.loading = false; }
        });
        this.billing.getLedger(0, 100).subscribe({
            next: (r) => { this.ledger = r?.content ?? []; },
            error: () => { this.ledger = []; }
        });
    }

    get statusLabel(): string {
        switch (this.summary?.status) {
            case 'OVERDRAWN': return 'Overdrawn';
            case 'LOW': return 'Low';
            case 'UNLIMITED': return 'Unlimited';
            default: return 'Healthy';
        }
    }

    get statusClass(): string {
        switch (this.summary?.status) {
            case 'OVERDRAWN': return 'over';
            case 'LOW': return 'low';
            case 'UNLIMITED': return 'unl';
            default: return 'ok';
        }
    }

    /** Bar width relative to the biggest slice, so the largest category fills the bar. */
    pct(amount: number): number {
        const amounts = (this.summary?.spend30d || []).map(s => s.amount || 0);
        const max = Math.max(1, ...amounts);
        return Math.max(2, Math.round(((amount || 0) / max) * 100));
    }
}
